import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  IconButton,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import rolesService from '../services/rolesService';
import PermissionMatrix from './PermissionMatrix';

const RoleFormModal = ({ open, onClose, role, onRoleSaved }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    is_active: true,
    permissions: [],
  });
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      loadPermissions();
      if (role) {
        setFormData({
          name: role.name || '',
          display_name: role.display_name || '',
          description: role.description || '',
          is_active: role.is_active !== undefined ? role.is_active : true,
          permissions: role.permissions?.map((p) => p.id) || [],
        });
      } else {
        setFormData({
          name: '',
          display_name: '',
          description: '',
          is_active: true,
          permissions: [],
        });
      }
      setSelectedTab(0);
      setError('');
    }
  }, [open, role]);

  const loadPermissions = async () => {
    setLoadingPermissions(true);
    try {
      const response = await rolesService.getPermissions({ group_by_module: true });
      if (response.success) {
        setPermissions(response.permissions || []);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      setError('Failed to load permissions');
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: e.target.type === 'checkbox' ? checked : value,
    });
  };

  const handlePermissionsChange = (selectedPermissions) => {
    setFormData({
      ...formData,
      permissions: selectedPermissions,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name || !formData.display_name) {
      setError('Name and Display Name are required');
      return;
    }

    // For create, format name as snake_case
    const submitData = {
      ...formData,
      name: role ? formData.name : formData.name.toLowerCase().replace(/\s+/g, '_'),
    };

    setLoading(true);
    try {
      let response;
      if (role) {
        response = await rolesService.updateRole(role.id, submitData);
      } else {
        response = await rolesService.createRole(submitData);
      }

      if (response.success) {
        if (onRoleSaved) {
          onRoleSaved();
        }
      }
    } catch (error) {
      console.error('Error saving role:', error);
      setError(error.response?.data?.message || 'Failed to save role');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ bgcolor: '#29204C', color: 'white', py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {role ? 'Edit Role' : 'Create New Role'}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Tabs */}
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 3,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: '#29204C' },
          '& .MuiTabs-indicator': { bgcolor: '#29204C' },
        }}
      >
        <Tab label="Basic Info" />
        <Tab label="Permissions" />
      </Tabs>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Tab 0: Basic Info */}
        {selectedTab === 0 && (
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              required
              label="Role Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., hr_manager"
              helperText="System name (lowercase with underscores)"
              disabled={!!role} // Cannot change name on edit
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              required
              label="Display Name"
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              placeholder="e.g., HR Manager"
              helperText="Human-readable name"
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the role's purpose and responsibilities..."
              sx={{ mb: 3 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleChange}
                  name="is_active"
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        )}

        {/* Tab 1: Permissions */}
        {selectedTab === 1 && (
          <Box>
            {loadingPermissions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <PermissionMatrix
                permissions={permissions}
                selectedPermissions={formData.permissions}
                onChange={handlePermissionsChange}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ bgcolor: '#29204C', '&:hover': { bgcolor: '#1a1430' } }}
        >
          {loading ? <CircularProgress size={24} /> : role ? 'Update Role' : 'Create Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleFormModal;
